import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gate MCQ/Tech terminal actions until session recording upload completes or
 * the candidate explicitly continues without recording.
 */
export function useRecordingUploadGate(stopAndUpload, prepareRecordingBlob) {
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFailedOpen, setUploadFailedOpen] = useState(false);
  const [lastUploadError, setLastUploadError] = useState(null);
  const pendingResolveRef = useRef(null);

  useEffect(() => () => {
    pendingResolveRef.current = null;
  }, []);

  useEffect(() => {
    if (!uploading) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [uploading]);

  const finishPending = useCallback((value) => {
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    setUploadFailedOpen(false);
    setUploading(false);
    setUploadProgress(null);
    if (resolve) resolve(value);
  }, []);

  const attemptUpload = useCallback(async () => {
    setUploading(true);
    setUploadProgress(0);
    setLastUploadError(null);
    try {
      const result = await stopAndUpload({
        onProgress: (pct) => setUploadProgress(pct),
      });
      if (!result.started || result.ok) {
        setUploading(false);
        setUploadProgress(null);
        return { proceed: true, skippedRecording: !result.started };
      }
      setLastUploadError(result.error || 'upload_failed');
      setUploading(false);
      setUploadProgress(null);
      return { proceed: false };
    } catch (e) {
      setLastUploadError(e?.message || 'upload_failed');
      setUploading(false);
      setUploadProgress(null);
      return { proceed: false };
    }
  }, [stopAndUpload]);

  /**
   * Resolves when upload succeeds, was skipped, or user continues without recording.
   * @param {{ auto?: boolean, expectRecording?: boolean }} opts
   */
  const ensureUploadBeforeTerminal = useCallback((opts = {}) => new Promise((resolve) => {
    const run = async () => {
      let first = await attemptUpload();
      if (opts.auto && opts.expectRecording && first.skippedRecording && prepareRecordingBlob) {
        await prepareRecordingBlob();
        first = await attemptUpload();
      }
      if (first.proceed) {
        resolve(first);
        return;
      }
      pendingResolveRef.current = resolve;
      setUploadFailedOpen(true);
    };
    run();
  }), [attemptUpload, prepareRecordingBlob]);

  const retryUpload = useCallback(async () => {
    const result = await attemptUpload();
    if (result.proceed) {
      finishPending(result);
    } else {
      setUploadFailedOpen(true);
    }
  }, [attemptUpload, finishPending]);

  const continueWithoutRecording = useCallback(() => {
    finishPending({ proceed: true, skippedRecording: true });
  }, [finishPending]);

  const cancelUploadPrompt = useCallback(() => {
    finishPending({ proceed: false, cancelled: true });
  }, [finishPending]);

  return {
    uploadProgress,
    uploading,
    uploadFailedOpen,
    lastUploadError,
    ensureUploadBeforeTerminal,
    retryUpload,
    continueWithoutRecording,
    cancelUploadPrompt,
  };
}
